import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostBinding,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewRef
} from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { TimeoutService } from '@df/ng/timeout.service';
import { fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, mergeMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
    selector: '[dragScroll]',
    standalone: true
})
export class DragScrollDirective implements OnChanges, OnInit, AfterViewInit, OnDestroy {
    @Input()
    scrollToIndex?: number;

    @Input()
    scrollIntoView?: boolean;

    @Input()
    dragscrollFade = true;

    @HostBinding('class.dragscroll')
    dragscroll = true;

    @HostBinding('class.dragscroll-start')
    dragscrollStart = true;

    @HostBinding('class.dragscroll-end')
    dragscrollEnd = false;

    @HostBinding('class.dragscroll-dragging')
    dragscrollDragging = false;

    @HostBinding('class.dragscroll-shadow')
    dragscrollShadow = false;

    @HostBinding('class.dragscroll-can-drag')
    dragscrollCanDrag = false;

    /**
     * If set to true we will not start directive on touchy devices
     */
    @Input()
    dragScrollNoTouchy = false;

    private readonly destroyed$ = new Subject<undefined>();
    private _mutationObserver!: MutationObserver;
    protected subscriptions: Subscription = new Subscription();
    protected move$!: Observable<MouseEvent | TouchEvent>;
    protected down$!: Observable<MouseEvent | TouchEvent>;
    protected leave$!: Observable<MouseEvent | TouchEvent>;
    protected isDown = false;
    protected isDragging = false;
    protected state?: boolean;
    protected startX!: number;
    protected scrollLeft!: number;
    protected wrapperOffsetWidth!: number;
    protected wrapperScrollWidth!: number;
    wrapper!: HTMLElement;

    // Performance optimizations
    private rafId?: number;
    private updateScheduled = false;
    private lastScrollLeft = -1;
    private lastWrapperOffsetWidth = -1;
    private lastWrapperScrollWidth = -1;

    // Throttled and debounced functions
    private checkThrottled: () => void;
    private checkDebounced: () => void;

    private meta = inject(Meta);

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected elementRef: ElementRef,
        protected ngZone: NgZone,
        protected renderer: Renderer2,
        protected app: App,
        protected client: Client,
        protected timeoutService: TimeoutService
    ) {
        // Initialize throttled and debounced functions
        this.checkThrottled = this.timeoutService.throttle(() => this.check(), 16); // ~60fps
        this.checkDebounced = this.timeoutService.debounce(() => this.check(), 100);

        this.meta.routerEvents
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this.destroyed$)
            )
            .subscribe(() => this.timeoutService.setTimeout(() => this._scrollToIndex(this.scrollToIndex), 100));
    }

    ngOnInit(): void {
        if (this.app.isPlatformBrowser && (!this.dragScrollNoTouchy || !this.client.isTouchy)) {
            this.wrapper = this.renderer.createElement('div');
            this.renderer.setStyle(this.wrapper, 'overflow', 'hidden');
            this.renderer.setStyle(this.wrapper, 'white-space', 'nowrap');
            this.renderer.setStyle(this.wrapper, 'vertical-align', 'top');

            this.wrapAll(this.elementRef.nativeElement, this.wrapper);

            this.init();

            this.subscriptions.add(this.client.obs.resize.subscribe(() => this.checkDebounced()));
        }
    }

    ngAfterViewInit(): void {
        if (this.app.isPlatformBrowser) {
            this._scrollToIndex(this.scrollToIndex);

            this.check();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.app.isPlatformBrowser && changes['scrollToIndex'] && this.wrapper) {
            this._scrollToIndex(this.scrollToIndex);
        }
    }

    ngOnDestroy(): void {
        // Cancel any pending RAF to prevent memory leaks
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = undefined;
        }

        // Disconnect MutationObserver
        if (this._mutationObserver) {
            this._mutationObserver.disconnect();
        }

        this.subscriptions.unsubscribe();
        this.destroyed$.next(undefined);
        this.destroyed$.complete();
    }

    protected init(): void {
        this.ngZone.runOutsideAngular(() => {
            // Create event streams with explicit passive: false for touch events to allow preventDefault
            this.down$ = merge(
                fromEvent<MouseEvent>(this.wrapper, 'mousedown'),
                this.createNonPassiveEvent<TouchEvent>(this.wrapper, 'touchstart')
            );
            this.leave$ = merge(
                fromEvent<MouseEvent>(this.wrapper, 'mouseleave'),
                fromEvent<MouseEvent>(document, 'mouseup'),
                this.createNonPassiveEvent<TouchEvent>(document, 'touchend')
            );
            this.move$ = merge(fromEvent<MouseEvent>(document, 'mousemove'), this.createNonPassiveEvent<TouchEvent>(document, 'touchmove'));

            this.subscriptions.add(
                this.down$
                    .pipe(
                        tap((event: MouseEvent | TouchEvent) => {
                            this.isDown = true;
                            const x: number = this.getEventPageX(event);

                            this.startX = x - this.wrapper.offsetLeft;
                            this.scrollLeft = this.wrapper.scrollLeft;
                        }),
                        mergeMap(_ =>
                            this.move$.pipe(
                                // complete inner observable on mouseleave event
                                takeUntil(this.leave$)
                            )
                        )
                    )
                    .subscribe(event => this.move(event))
            );

            this.subscriptions.add(
                this.leave$
                    .pipe(
                        tap(() => {
                            this.isDown = false;
                            this.isDragging = false;
                            this.dragscrollDragging = false;
                            this.detectChanges();
                        })
                    )
                    .subscribe()
            );

            if (typeof MutationObserver !== 'undefined') {
                this._mutationObserver = new MutationObserver(() => this.checkThrottled());
                this._mutationObserver.observe(this.wrapper, {
                    childList: true,
                    attributes: true,
                    subtree: true
                });
            }

            this.subscriptions.add(
                resizeObservable(this.wrapper, () => {
                    this.checkThrottled();
                }).subscribe()
            );
        });
    }

    protected move(event: MouseEvent | TouchEvent): void {
        if (!this.isDown || !this.dragscrollCanDrag) {
            return;
        }

        if (!this.isDragging) {
            this.isDragging = true;
            this.dragscrollDragging = true;
            this.detectChanges();
        }

        // Prevent default behavior to avoid text selection and scrolling
        // This is safe now because we use non-passive event listeners
        event.preventDefault();

        const pgX: number = this.getEventPageX(event);
        const x = pgX - this.wrapper.offsetLeft;
        const walk = x - this.startX;
        const newScrollLeft = this.scrollLeft - walk;

        // Use RAF for smooth scrolling and to batch DOM updates
        if (!this.updateScheduled) {
            this.updateScheduled = true;
            requestAnimationFrame(() => {
                this.wrapper.scrollLeft = newScrollLeft;
                this.applyClass();
                this.updateScheduled = false;
            });
        }
    }

    protected check(): void {
        // Cancel any pending RAF to prevent multiple updates
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }

        // Schedule update using RAF to batch DOM reads/writes
        this.rafId = requestAnimationFrame(() => {
            if (!this.wrapper) return;

            const wrapperOffsetWidth = this.wrapper.offsetWidth;
            const wrapperScrollWidth = this.wrapper.scrollWidth;

            // Only update if dimensions actually changed to prevent unnecessary reflow
            if (this.lastWrapperOffsetWidth !== wrapperOffsetWidth || this.lastWrapperScrollWidth !== wrapperScrollWidth) {
                this.lastWrapperOffsetWidth = wrapperOffsetWidth;
                this.lastWrapperScrollWidth = wrapperScrollWidth;
                this.wrapperOffsetWidth = wrapperOffsetWidth;
                this.wrapperScrollWidth = wrapperScrollWidth;

                const canDrag = this.wrapperOffsetWidth < this.wrapperScrollWidth;
                const shouldShowShadow = canDrag && this.dragscrollFade;

                // Batch class updates to prevent multiple reflows
                if (this.dragscrollCanDrag !== canDrag || this.dragscrollShadow !== shouldShowShadow) {
                    this.dragscrollCanDrag = canDrag;
                    this.dragscrollShadow = shouldShowShadow;
                    this.detectChanges();
                }
            }

            this.rafId = undefined;
        });
    }

    protected getEventPageX(event: MouseEvent | TouchEvent): number {
        if (event instanceof MouseEvent) {
            return event.pageX;
        }

        if (event instanceof TouchEvent) {
            return event.touches[0].pageX;
        }

        return 0;
    }

    protected applyClass(): void {
        // Cache scroll position to prevent multiple DOM reads
        const scrollLeft = this.wrapper.scrollLeft;

        // Only update if scroll position changed significantly to prevent excessive updates
        if (Math.abs(this.lastScrollLeft - scrollLeft) > 5) {
            this.lastScrollLeft = scrollLeft;

            if (scrollLeft < 10) {
                this.toggleClass(true);
            } else if (this.wrapperScrollWidth - scrollLeft < this.wrapperOffsetWidth + 10) {
                this.toggleClass(false);
            } else {
                this.toggleClass();
            }
        }
    }

    protected toggleClass(start?: boolean): void {
        if (this.state !== start) {
            this.state = start;

            if (start !== undefined) {
                if (start) {
                    this.dragscrollStart = true;
                    this.dragscrollEnd = false;
                } else {
                    this.dragscrollEnd = true;
                    this.dragscrollStart = false;
                }
            } else {
                this.dragscrollEnd = false;
                this.dragscrollStart = false;
            }

            this.detectChanges();
        }
    }

    protected _scrollToIndex(index?: number): void {
        if (index !== undefined && this.wrapper) {
            const childrenElement = this.wrapper.children[index] as HTMLElement;

            if (childrenElement) {
                if (this.scrollIntoView) {
                    childrenElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                } else {
                    this.wrapper.scrollLeft = childrenElement.offsetLeft;
                }
            }
        }
    }

    protected wrapAll(target: HTMLElement, wrapper: HTMLElement): HTMLElement {
        Array.from(target.childNodes).forEach(child => wrapper.appendChild(child));
        target.appendChild(wrapper);

        return wrapper;
    }

    /**
     * Creates a non-passive event observable to allow preventDefault
     */
    private createNonPassiveEvent<T extends Event>(target: EventTarget, eventName: string): Observable<T> {
        return new Observable<T>(subscriber => {
            const handler = (event: T) => subscriber.next(event);

            // Add event listener with explicit passive: false
            target.addEventListener(eventName, handler as EventListener, {
                passive: false,
                capture: false
            });

            return () => {
                target.removeEventListener(eventName, handler as EventListener);
            };
        });
    }

    protected detectChanges(): void {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }
}
